import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  FileTextIcon,
  PencilIcon,
  RefreshCwIcon,
  Trash2Icon,
  UploadCloudIcon,
  XIcon,
} from '../components/icons.js';
import {
  CANONICAL_SPECIALISATIONS,
  sanitizeUploadFilename,
  type DocumentRegistryEntry,
  type IngestJob,
  type Specialisation,
} from '@kennisbank/shared';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';
import { BatchProgressPanel } from '../components/admin/BatchProgressPanel.js';
import { BulkUploadPreflight } from '../components/admin/BulkUploadPreflight.js';
import { useBulkUpload, type DroppedFile } from '../hooks/useBulkUpload.js';
import { SortableTh, toggleSort } from '../components/SortableTh.js';
import { InlineSpecialisationsCell } from '../components/InlineSpecialisationsCell.js';
import { DocumentVersionsPanel } from '../components/DocumentVersionsPanel.js';
import { Icon } from '../components/Icon.js';

/**
 * Slice 5 commit 4 — Knowledgebase Admin page.
 *
 * Andre's flow:
 *   1. Drop one or more files in the upload zone (or click to pick).
 *   2. Each file gets a progress strip: Uploading → Validating → Indexing → Done.
 *   3. On filename collision, a confirm modal asks before overwriting.
 *   4. The document list shows every existing doc with chunkCount + last-ingested.
 *      Per-row: re-ingest, delete (type-to-confirm with chunk count).
 *   5. Single global ingest lock — UI disables the upload zone with a
 *      "Waiting for current indexing…" banner whenever a job is running.
 *
 * RBAC: route is mounted behind RoleGuard for `kb_admin`. Backend
 * re-checks. We render a friendly "No access" message if the user somehow
 * lands here without the role.
 */

const ALLOWED_MIME: Record<string, true> = {
  'application/pdf': true,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': true,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': true,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': true,
  'application/msword': true,
  'application/vnd.ms-excel': true,
  'application/vnd.ms-powerpoint': true,
  'application/vnd.oasis.opendocument.text': true,
  'application/vnd.oasis.opendocument.spreadsheet': true,
  'application/vnd.ms-visio.drawing': true,
  'application/vnd.visio': true,
  'application/json': true,
  'text/csv': true,
  'application/csv': true,
  'image/jpeg': true,
  'image/png': true,
  'image/webp': true,
  'image/gif': true,
};

// Extension → canonical MIME. Used when the browser reports empty
// `file.type` (common for .vsdx, .json, .csv on some OSes) so we still
// send the backend a valid Content-Type for the signed URL.
const EXT_TO_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ppt: 'application/vnd.ms-powerpoint',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  vsdx: 'application/vnd.ms-visio.drawing',
  vsd: 'application/vnd.visio',
  json: 'application/json',
  csv: 'text/csv',
  md: 'text/markdown',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
};
const ALLOWED_EXTS = new Set(Object.keys(EXT_TO_MIME));
const extOf = (name: string): string => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
};
/** MIME that should be sent to the backend / GCS for this file. Falls
 * back to the extension table when the browser reports nothing. */
const resolveContentType = (file: File): string =>
  file.type && ALLOWED_MIME[file.type]
    ? file.type
    : EXT_TO_MIME[extOf(file.name)] || 'application/octet-stream';

const MAX_BYTES = 100 * 1024 * 1024;

const OS_NOISE_FILES = new Set(['thumbs.db', 'desktop.ini']);
const isOsNoiseFile = (name: string): boolean =>
  name.startsWith('.') || OS_NOISE_FILES.has(name.toLowerCase());

// ── Folder upload (drag a folder, or "choose a folder") ──────────────────
// Drag-drop and the directory picker both hand us a tree; these walk it into
// a flat File[]. The Entry API (webkitGetAsEntry) is non-standard but
// universally supported; we fall back to the flat dataTransfer.files when a
// browser doesn't expose it (so plain multi-file drops still work).
function readAllDirEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = [];
    const next = (): void => {
      // readEntries returns at most ~100 per call — loop until it drains.
      reader.readEntries((batch) => {
        if (batch.length === 0) resolve(all);
        else {
          all.push(...batch);
          next();
        }
      }, reject);
    };
    next();
  });
}
// Keeps the entry's path inside the drop (`fullPath`, e.g. `/Policies/a.pdf`)
// — drag-drop Files have an empty webkitRelativePath, and the bulk planner
// needs the folder names to tell `Q1/report.pdf` from `Q2/report.pdf`.
async function entryToFiles(entry: FileSystemEntry): Promise<DroppedFile[]> {
  if (entry.isFile) {
    return new Promise<DroppedFile[]>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(
        (f) => resolve([{ file: f, relativePath: entry.fullPath.replace(/^\/+/, '') }]),
        reject,
      ),
    );
  }
  if (entry.isDirectory) {
    const entries = await readAllDirEntries((entry as FileSystemDirectoryEntry).createReader());
    const nested = await Promise.all(entries.map(entryToFiles));
    return nested.flat();
  }
  return [];
}

/**
 * Backend `error` code → user-facing English message. Anything not in
 * the map falls back to the server-supplied `message` field.
 */
const ERROR_MESSAGES: Record<string, string> = {
  unsupported_mime:
    'File format not supported. Use PDF, DOCX, PPTX, ODT, VSDX, JSON, CSV or an image.',
  too_large: 'File too large. Maximum 100 MB.',
  too_small: 'File is empty.',
  md5_mismatch: 'Upload failed — checksum mismatch. Please try again.',
  pdf_password_protected: 'PDF is password-protected. Remove the password and try again.',
  pdf_corrupt: 'PDF appears damaged or unreadable.',
  object_missing: 'Uploaded file not found in storage.',
  filename_collision:
    'A file with this name already exists. Click "Overwrite & retry" to replace it.',
  ingest_lock_held:
    'An indexing job is running. Edits and deletes are refused until it finishes — try again shortly.',
  invoke_failed: 'Indexing could not be started. Please try again.',
  no_extractable_text:
    'No text found in this document — likely an image-only PDF (scanned). Use a PDF with selectable text, or convert to DOCX/PPTX/ODT.',
  extraction_failed: 'Text extraction failed. Check that the file is not damaged and try again.',
  rate_limited: 'Too many requests to the embedding service. Wait a minute and try again.',
  ingest_failed: 'Indexing failed. Please try again or upload a different format.',
  staging_expired:
    'The uploaded file expired from staging (after 24 hours) before it was indexed. Upload it again.',
  worker_lost:
    'Indexing stopped unexpectedly (worker crash or timeout). Upload the file again or reindex it.',
  upload_abandoned: 'The file was never uploaded (the browser tab closed before the upload).',
  bad_request: 'Request not valid.',
  not_found: 'Document not found.',
  confirm_mismatch: 'Filename does not match. Type the exact same name.',
  forbidden: 'No access to the Knowledgebase Admin.',
};

function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err instanceof ApiError) {
    if (ERROR_MESSAGES[err.code]) return ERROR_MESSAGES[err.code];
    // Zod 400s carry a `validation_error` code with a flatten()'d detail
    // whose `fieldErrors` values are the messages from the backend
    // schema. Surface the first one rather than the generic "Request
    // payload failed validation" shell.
    if (err.code === 'validation_error' && err.detail && typeof err.detail === 'object') {
      const fe = (err.detail as { fieldErrors?: Record<string, string[] | undefined> }).fieldErrors;
      const firstField = fe ? Object.values(fe).find((v) => v && v.length > 0) : undefined;
      if (firstField && firstField[0]) return firstField[0];
    }
    return err.message ?? fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

interface UploadUrlResponse {
  jobId: string;
  uploadUrl: string;
  gcsPath: string;
  expiresAt: string;
}

type QueueStage =
  | 'queued'
  | 'uploading'
  | 'validating'
  | 'waiting' // ingest-lock-held; auto-retrying every ~10s
  | 'indexing'
  | 'done'
  | 'error';

interface QueueItem {
  id: string;
  /** Stable display name. Always present, including for items hydrated
   *  from sessionStorage where the underlying File is gone. */
  filename: string;
  /** Live File reference. Undefined for items restored from sessionStorage
   *  after navigation — those can't be retried (no source bytes to PUT
   *  again) but their backend progress is still pollable via jobId. */
  file?: File;
  stage: QueueStage;
  jobId?: string;
  errorMessage?: string;
  /** Backend error.code, set on stage='error'. Drives whether the row
   *  shows the "Keep as download only" escape hatch. */
  errorCode?: string;
  progressPct?: number;
  /** Which corpus tier this upload targets. ring0 = OneNebula internal
   *  (handbook, policies — RBAC-gated). ring1 = product / customer-facing
   *  (broader access). Set at drop time; persisted on retries. */
  ring: 'ring0' | 'ring1';
  /** ISO 27001 classification set at intake. Locked to `confidential`
   *  for the Confidential drop zone; defaults to internal otherwise. */
  classification?: 'public' | 'internal' | 'confidential' | 'restricted';
  /** Access groups the chunks must intersect for retrieval. Required when
   *  `classification` is confidential/restricted. */
  accessGroups?: string[];
  /** Sub-classification within confidential (internal / vendor / client).
   *  Ignored when classification != confidential. */
  confidentialKind?: 'internal' | 'vendor' | 'client';
  /** Sub-classification within product (oneview / stactize / surge).
   *  Ignored when classification is confidential. */
  productKind?: 'oneview' | 'stactize' | 'surge';
}

/**
 * Errors where the file uploaded fine but extraction couldn't pull text
 * (image-only PDFs, broken text layer, corrupt PDFs). For these the
 * operator can opt to register the doc as download-only — searchable
 * via /documenten but invisible to the agent. Other failure modes
 * (rate_limited, ingest_failed, invoke_failed) are transient/infra and
 * should NOT offer this escape hatch.
 */
const KEEP_AS_DOWNLOAD_ELIGIBLE_CODES = new Set([
  'no_extractable_text',
  'extraction_failed',
  'pdf_corrupt',
]);

const QUEUE_STORAGE_KEY = 'kennisbank.uploadQueue';

/** Wire shape persisted to sessionStorage. Strips the File. */
interface PersistedQueueItem {
  id: string;
  filename: string;
  stage: QueueStage;
  jobId?: string;
  errorMessage?: string;
  progressPct?: number;
  ring?: 'ring0' | 'ring1';
}

interface PendingCollision {
  files: File[];
  resolve: (overwrite: boolean) => void;
}

export function AdminKnowledgebasePage(): JSX.Element {
  const [documents, setDocuments] = useState<DocumentRegistryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [collision, setCollision] = useState<PendingCollision | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DocumentRegistryEntry | null>(null);
  const [reload, setReload] = useState(0);
  const [lockHeld, setLockHeld] = useState(false);
  // Default: newest-first by last ingest. Andre's most common task is
  // verifying / re-ingesting / fixing the doc she just uploaded — bringing
  // it to the top removes a click and a scan every time.
  const [sortBy, setSortBy] = useState<'filename' | 'author' | 'chunkCount' | 'lastIngestedAt'>(
    'lastIngestedAt',
  );
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Source-kind filter — Knowledgebase Admin's primary mode is "manage the
  // PDFs / DOCX / spreadsheets I uploaded", so the default hides files
  // that were ingested by the Confluence sync and the Code Sources sync.
  // Those still live in the same registry but have their own admin pages.
  // Toggle to see them inline. 'all' lets the operator spot-check the
  // whole corpus on one screen.
  const [sourceKind, setSourceKind] = useState<'uploads' | 'confluence' | 'code' | 'all'>(
    'uploads',
  );
  const [ringFilter, setRingFilter] = useState<'all' | 'ring0' | 'ring1'>('all');
  // ISO 27001 classification filter. Docs without an explicit `classification`
  // field default to 'internal' (matches the practitioner /documents page).
  const [classificationFilter, setClassificationFilter] = useState<ClassificationFilter>('all');
  // Sub-filter only meaningful when classificationFilter === 'confidential'.
  // 'all' = every confidential doc regardless of kind. Picking a specific
  // kind narrows the table to that bucket. Resets to 'all' when the
  // operator leaves the Confidential tier.
  const [confidentialKindFilter, setConfidentialKindFilter] = useState<
    'all' | 'internal' | 'vendor' | 'client'
  >('all');
  // Sub-filter for product tier (ring1 non-confidential). Only meaningful
  // when classificationFilter is 'all' or non-confidential. 'all' shows
  // every product doc; picking a kind narrows to that product line.
  const [productKindFilter, setProductKindFilter] = useState<
    'all' | 'oneview' | 'stactize' | 'surge'
  >('all');
  const [searchQ, setSearchQ] = useState('');
  // Document-version families review (own component; collapsed by default).
  const [showVersions, setShowVersions] = useState(false);
  // Client-side pagination — the registry list endpoint returns every
  // ring0 + ring1 doc in one shot (no server-side paging yet), which is
  // fine at corpus sizes < ~5K but renders all rows into the DOM and
  // makes filter / sort feel sluggish. Slicing the already-
  // filtered+sorted array for render caps DOM work at `pageSize`
  // regardless of corpus size. Any filter / sort change resets to page 1.
  const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
  type PageSizeOpt = (typeof PAGE_SIZE_OPTIONS)[number];
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOpt>(50);
  // Single-flight upload chain. Every enqueueFiles call (drop, retry,
  // file-picker) appends to this promise so new batches queue behind any
  // in-flight item rather than racing the global ingest lock.
  const queueChainRef = useRef<Promise<void>>(Promise.resolve());
  // Cancellation infra for "Remove from queue" before the Cloud Run job
  // fires. The chain still walks every item it captured at enqueue time;
  // a cancelled id makes processOne short-circuit at the next phase
  // boundary instead of plumbing item removal through the running loop.
  // xhr map lets us abort an in-flight GCS PUT immediately.
  const cancelledIdsRef = useRef<Set<string>>(new Set());
  const xhrByIdRef = useRef<Map<string, XMLHttpRequest>>(new Map());
  // Chunks-inspection modal — Andre clicks a doc's chunkCount to spot-check
  // extraction quality. Holds the filename being inspected; null = closed.
  const [inspectChunks, setInspectChunks] = useState<DocumentRegistryEntry | null>(null);
  // Live specialisation vocabulary from /api/v1/admin/specialisations.
  // Fetched once on mount so each row's popover can list admin-defined
  // specs without an extra round-trip per click.
  const [specialisations, setSpecialisations] = useState<Specialisation[]>([]);
  // Per-row save flag for the Specialisations cell. We only ever edit one
  // row at a time, so a single string is enough.
  const [savingFilename, setSavingFilename] = useState<string | null>(null);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTopError(null);
    apiGet<{ documents: DocumentRegistryEntry[] }>('/api/v1/admin/knowledgebase/documents')
      .then((res) => {
        if (!cancelled) setDocuments(res.documents);
      })
      .catch((err: unknown) => {
        if (!cancelled) setTopError(errorMessage(err, 'Could not load document list.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  // Page-level fetch of the specialisation vocabulary. Once on mount —
  // the list is small (~dozens) and shared across every row's popover.
  // A failure here degrades gracefully: the Specialisations cell falls
  // back to the hardcoded CANONICAL_* names baked into InlineSpecialisationsCell.
  useEffect(() => {
    let cancelled = false;
    apiGet<{ specs: Specialisation[] }>('/api/v1/admin/specialisations')
      .then((res) => {
        if (!cancelled) setSpecialisations(res.specs);
      })
      .catch(() => {
        // Silent: the cell still works against the hardcoded vocabulary.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateQueueItem = useCallback((id: string, patch: Partial<QueueItem>) => {
    setQueue((items) => items.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  }, []);

  // Remove a queued/in-flight upload before the Cloud Run job fires. Marks
  // the id so processOne's phase-boundary guards short-circuit, aborts any
  // GCS PUT in flight, and drops the row from state. Validating/indexing
  // are intentionally out of scope — the backend job is already running by
  // then; the user should let it finish and delete via the registry table.
  const cancelQueueItem = useCallback((id: string): void => {
    cancelledIdsRef.current.add(id);
    const xhr = xhrByIdRef.current.get(id);
    if (xhr) {
      try {
        xhr.abort();
      } catch {
        /* xhr already finished; abort is a no-op */
      }
      xhrByIdRef.current.delete(id);
    }
    setQueue((cur) => cur.filter((q) => q.id !== id));
  }, []);

  // Persist a slim view of the queue on every change. File objects are
  // stripped (Blobs don't survive JSON.stringify); items WITHOUT a jobId
  // are also stripped because there's no way to recover their backend
  // state without re-dropping the original File. Andre navigating away
  // mid-PUT loses those — acceptable; the indexed jobs survive.
  useEffect(() => {
    const persisted: PersistedQueueItem[] = queue
      .filter((q) => q.jobId !== undefined || q.stage === 'done' || q.stage === 'error')
      .map((q) => ({
        id: q.id,
        filename: q.filename,
        stage: q.stage,
        jobId: q.jobId,
        errorMessage: q.errorMessage,
        progressPct: q.progressPct,
        ring: q.ring,
      }));
    if (persisted.length > 0) {
      sessionStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(persisted));
    } else {
      sessionStorage.removeItem(QUEUE_STORAGE_KEY);
    }
  }, [queue]);

  const pollJob = useCallback(
    async (queueId: string, jobId: string): Promise<void> => {
      const start = Date.now();
      // Poll up to 60 min. Per-doc target is <60s for PDF/DOCX, but
      // multi-page Visio files (.vsdx) caption every page through Vertex
      // Vision serially (~6-8 min for 40 pages), and since the server-side
      // queue a file may first wait behind a folder upload.
      while (Date.now() - start < 60 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2000));
        let job: IngestJob;
        try {
          job = await apiGet<IngestJob>(`/api/v1/admin/knowledgebase/jobs/${jobId}`);
        } catch (err) {
          updateQueueItem(queueId, { stage: 'error', errorMessage: errorMessage(err) });
          return;
        }
        if (job.status === 'succeeded') {
          updateQueueItem(queueId, { stage: 'done' });
          return;
        }
        if (job.status === 'queued') {
          updateQueueItem(queueId, { stage: 'waiting', progressPct: 70 });
          continue;
        }
        if (job.status === 'invoking' || job.status === 'running') {
          updateQueueItem(queueId, { stage: 'indexing', progressPct: 80 });
          continue;
        }
        if (job.status === 'failed' || job.status === 'cancelled' || job.status === 'expired') {
          updateQueueItem(queueId, {
            stage: 'error',
            errorCode: job.errorCode,
            errorMessage: ERROR_MESSAGES[job.errorCode ?? ''] ?? 'Indexing failed.',
          });
          return;
        }
      }
      updateQueueItem(queueId, {
        stage: 'error',
        errorMessage:
          'Still indexing — this can happen for very large diagrams. Refresh the documents list in a few minutes; the file may already be there.',
      });
    },
    [updateQueueItem],
  );

  // On mount: hydrate the queue from sessionStorage. Items with a jobId
  // and a non-terminal stage get a fresh pollJob attached so Andre's
  // wachtrij keeps ticking after she navigates back. Done/error items
  // render as-is so she sees the historical outcome.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const raw = sessionStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return;
    try {
      const persisted = JSON.parse(raw) as PersistedQueueItem[];
      if (!Array.isArray(persisted) || persisted.length === 0) return;
      const restored: QueueItem[] = persisted.map((p) => ({
        id: p.id,
        filename: p.filename,
        stage: p.stage,
        jobId: p.jobId,
        errorMessage: p.errorMessage,
        progressPct: p.progressPct,
        ring: p.ring ?? 'ring1',
      }));
      setQueue((cur) => [...cur, ...restored]);
      // Re-attach polls for anything still in flight server-side.
      restored.forEach((q) => {
        if (
          q.jobId &&
          (q.stage === 'indexing' || q.stage === 'validating' || q.stage === 'uploading')
        ) {
          void pollJob(q.id, q.jobId);
        }
      });
    } catch {
      sessionStorage.removeItem(QUEUE_STORAGE_KEY);
    }
  }, [pollJob]);

  const processOne = useCallback(
    async (qItem: QueueItem, overwrite: boolean): Promise<void> => {
      const file = qItem.file;
      if (!file) {
        // Restored items from sessionStorage have no File reference; the
        // caller's retry-button gate (canRetry={q.file !== undefined}) is
        // supposed to prevent this branch. Belt-and-braces.
        updateQueueItem(qItem.id, {
          stage: 'error',
          errorMessage: 'File no longer available — drag it in again.',
        });
        return;
      }
      // Cancelled before the chain reached this item (queued → user clicked
      // Cancel). The chain captured the item array at enqueue time, so it
      // still walks the slot; the cancellation flag is what makes us skip.
      if (cancelledIdsRef.current.has(qItem.id)) return;
      try {
        updateQueueItem(qItem.id, { stage: 'uploading', progressPct: 5 });
        const contentType = resolveContentType(file);
        const mint = await apiPost<UploadUrlResponse>(
          '/api/v1/admin/knowledgebase/documents/upload-url',
          {
            filename: qItem.filename,
            contentType,
            sizeBytes: file.size,
            overwrite,
            ring: qItem.ring,
            ...(qItem.classification ? { classification: qItem.classification } : {}),
            ...(qItem.accessGroups && qItem.accessGroups.length > 0
              ? { accessGroups: qItem.accessGroups }
              : {}),
            ...(qItem.confidentialKind ? { confidentialKind: qItem.confidentialKind } : {}),
            ...(qItem.productKind ? { productKind: qItem.productKind } : {}),
          },
        );
        // Cancelled while upload-url was in flight. The ingest_jobs/{id}
        // doc we just minted stays in `pending` and the staging signed URL
        // expires unused — both garbage-collect via the 24h staging bucket
        // lifecycle rule.
        if (cancelledIdsRef.current.has(qItem.id)) return;

        // Direct PUT to the signed GCS URL — bypasses the backend's 32MB
        // request-body cap. Content-Type must match the signature.
        try {
          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhrByIdRef.current.set(qItem.id, xhr);
            xhr.open('PUT', mint.uploadUrl);
            xhr.setRequestHeader('Content-Type', contentType);
            xhr.upload.onprogress = (ev) => {
              if (ev.lengthComputable) {
                const pct = Math.min(60, 5 + Math.round((ev.loaded / ev.total) * 55));
                updateQueueItem(qItem.id, { progressPct: pct });
              }
            };
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) resolve();
              else reject(new Error(`upload HTTP ${xhr.status}`));
            };
            xhr.onerror = () => reject(new Error('network error during upload'));
            xhr.onabort = () => reject(new Error('upload cancelled'));
            xhr.send(file);
          });
        } finally {
          xhrByIdRef.current.delete(qItem.id);
        }
        // Cancelled during the PUT — abort fires the reject above; cancelled
        // *just after* the PUT resolved hits this guard. Either way: don't
        // finalize, the staged object will be lifecycle-deleted.
        if (cancelledIdsRef.current.has(qItem.id)) return;

        updateQueueItem(qItem.id, {
          stage: 'validating',
          jobId: mint.jobId,
          progressPct: 65,
        });

        // Finalize with cross-session lock auto-retry. If a previous tab/
        // session left a job running and the global ingest lock is held,
        // sit in 'waiting' state and retry every ~10s rather than dumping
        // a red error on Andre's screen. Total budget ~5 min — past that
        // give up and surface the error so she can investigate.
        const FINALIZE_MAX_ATTEMPTS = 30;
        const FINALIZE_RETRY_MS = 10_000;
        let finalizeAttempt = 0;
        while (true) {
          if (cancelledIdsRef.current.has(qItem.id)) return;
          try {
            await apiPost<{ jobId: string; status: string }>(
              `/api/v1/admin/knowledgebase/documents/${mint.jobId}/finalize`,
              {},
            );
            break;
          } catch (err) {
            const isLockHeld = err instanceof ApiError && err.code === 'ingest_lock_held';
            if (!isLockHeld) throw err;
            finalizeAttempt += 1;
            if (finalizeAttempt >= FINALIZE_MAX_ATTEMPTS) throw err;
            updateQueueItem(qItem.id, {
              stage: 'waiting',
              errorMessage: undefined,
              progressPct: 65,
            });
            setLockHeld(true);
            await new Promise((r) => setTimeout(r, FINALIZE_RETRY_MS));
          }
        }
        setLockHeld(false);
        if (cancelledIdsRef.current.has(qItem.id)) return;

        updateQueueItem(qItem.id, { stage: 'indexing', progressPct: 80 });
        await pollJob(qItem.id, mint.jobId);
      } catch (err) {
        // Cancellation surfaces as an XHR abort or a noisy mid-flight error.
        // The queue row was already removed from state by cancelQueueItem;
        // swallow so we don't paint a red error onto something the user
        // explicitly killed.
        if (cancelledIdsRef.current.has(qItem.id)) return;
        if (err instanceof ApiError && err.code === 'ingest_lock_held') {
          setLockHeld(true);
        }
        updateQueueItem(qItem.id, {
          stage: 'error',
          errorMessage: errorMessage(err),
          errorCode: err instanceof ApiError ? err.code : undefined,
        });
      }
    },
    [pollJob, updateQueueItem],
  );

  const enqueueFiles = useCallback(
    async (
      files: File[],
      ring: 'ring0' | 'ring1' = 'ring1',
      classification?: 'public' | 'internal' | 'confidential' | 'restricted',
      accessGroups?: string[],
      confidentialKind?: ConfidentialKind,
      productKind?: ProductKind,
    ): Promise<void> => {
      const valid: File[] = [];
      const errors: { name: string; reason: string }[] = [];
      for (const f of files) {
        const mimeOk = !!f.type && !!ALLOWED_MIME[f.type];
        const extOk = ALLOWED_EXTS.has(extOf(f.name));
        if (!mimeOk && !extOk) {
          errors.push({ name: f.name, reason: ERROR_MESSAGES.unsupported_mime });
          continue;
        }
        if (f.size > MAX_BYTES) {
          errors.push({ name: f.name, reason: ERROR_MESSAGES.too_large });
          continue;
        }
        if (f.size <= 0) {
          errors.push({ name: f.name, reason: ERROR_MESSAGES.too_small });
          continue;
        }
        valid.push(f);
      }
      if (errors.length > 0) {
        setTopError(errors.map((e) => `${e.name}: ${e.reason}`).join(' · '));
      }
      if (valid.length === 0) return;

      // Filename-collision check (one prompt for the whole batch). Names are
      // sanitised the same way the bulk planner does, so a character the
      // server rejects becomes `_` instead of a failed upload.
      const existingNames = new Set(documents.map((d) => d.filename.toLowerCase()));
      const colliding = valid.filter((f) =>
        existingNames.has(sanitizeUploadFilename(f.name).toLowerCase()),
      );
      let overwrite = false;
      if (colliding.length > 0) {
        overwrite = await new Promise<boolean>((resolve) => {
          setCollision({ files: colliding, resolve });
        });
        if (!overwrite) {
          // Drop colliding files; keep non-colliding ones.
          const keep = valid.filter(
            (f) => !existingNames.has(sanitizeUploadFilename(f.name).toLowerCase()),
          );
          if (keep.length === 0) return;
          valid.length = 0;
          valid.push(...keep);
        }
      }

      const items: QueueItem[] = valid.map((f) => ({
        id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        filename: sanitizeUploadFilename(f.name),
        file: f,
        stage: 'queued',
        ring,
        ...(classification ? { classification } : {}),
        ...(accessGroups && accessGroups.length > 0 ? { accessGroups } : {}),
        ...(confidentialKind ? { confidentialKind } : {}),
        ...(productKind ? { productKind } : {}),
      }));
      setQueue((cur) => [...cur, ...items]);

      // Append to the single-flight chain. Two scenarios:
      //   1. Chain idle → our work runs immediately.
      //   2. Chain busy → our items wait in 'queued' state until the
      //      previous batch's last item finishes its Cloud Run job
      //      (lock released), then we pick up.
      // Catch-then-ignore so one batch's failure doesn't break the chain
      // for the next; per-item errors are already captured in queue state.
      queueChainRef.current = queueChainRef.current
        .catch(() => undefined)
        .then(async () => {
          for (const item of items) {
            await processOne(item, overwrite);
          }
          setLockHeld(false);
          refresh();
        });
    },
    [documents, processOne, refresh],
  );

  // Drag/drop handlers moved into UploadZone — each zone owns its own
  // dragOver state so the user sees which one is active during a drop.

  // Folder / multi-file drops go through the bulk flow (pre-flight summary,
  // content dedup, server-side queue + batch progress). A single loose file
  // keeps the per-file queue row above, which offers the specialisation
  // picker on "Download only".
  const knownNames = useMemo(
    () => new Set(documents.map((d) => d.filename.toLowerCase())),
    [documents],
  );
  const bulk = useBulkUpload({
    knownNames,
    maxBytes: MAX_BYTES,
    contentTypeOf: resolveContentType,
    onError: setTopError,
  });
  const onZoneFiles = useCallback(
    (
      items: DroppedFile[],
      folder: boolean,
      ring: 'ring0' | 'ring1',
      classification?: 'public' | 'internal' | 'confidential' | 'restricted',
      accessGroups?: string[],
      confidentialKind?: ConfidentialKind,
      productKind?: ProductKind,
    ): void => {
      if (!folder && items.length === 1) {
        void enqueueFiles(
          items.map((i) => i.file),
          ring,
          classification,
          accessGroups,
          confidentialKind,
          productKind,
        );
        return;
      }
      const topFolder = folder ? items[0]?.relativePath.split('/')[0] : undefined;
      const label = topFolder ? `Folder “${topFolder}”` : `${items.length} files`;
      void bulk.start(
        items,
        { ring, classification, accessGroups, confidentialKind, productKind },
        label,
      );
    },
    [bulk, enqueueFiles],
  );

  /**
   * Slice 6 commit 4 — patch a single metadata field on a doc.
   * Optimistic update: writes the new value into local state immediately,
   * then sends the PATCH. On error, rolls back to the prior value and
   * surfaces the Dutch error string. The 6 editable fields all share this
   * helper since the backend route validates/normalises uniformly.
   */
  const patchMetadata = useCallback(
    async (
      filename: string,
      field:
        | 'author'
        | 'coAuthor'
        | 'documentTitle'
        | 'institution'
        | 'workingGroup'
        | 'documentType',
      newValue: string,
    ): Promise<void> => {
      const trimmed = newValue.trim();
      // Snapshot the current row in case we need to roll back.
      const prior = documents.find((d) => d.filename === filename);
      if (!prior) return;
      const priorValue = (prior as unknown as Record<string, string | undefined>)[field];
      // Optimistic update.
      setDocuments((cur) =>
        cur.map((d) =>
          d.filename === filename
            ? ({ ...d, [field]: trimmed || undefined } as DocumentRegistryEntry)
            : d,
        ),
      );
      try {
        await apiPatch(`/api/v1/admin/knowledgebase/documents/${encodeURIComponent(filename)}`, {
          [field]: trimmed === '' ? null : trimmed,
        });
      } catch (err) {
        // Roll back to prior value.
        setDocuments((cur) =>
          cur.map((d) =>
            d.filename === filename ? ({ ...d, [field]: priorValue } as DocumentRegistryEntry) : d,
          ),
        );
        if (err instanceof ApiError && err.code === 'ingest_lock_held') {
          setLockHeld(true);
        }
        setTopError(errorMessage(err, 'Update failed.'));
        throw err;
      }
    },
    [documents],
  );

  /**
   * Per-row product-kind save. Same shape as patchConfidentialKind but for
   * ring1 non-confidential docs (oneview / stactize / surge).
   */
  const patchProductKind = useCallback(
    async (filename: string, kind: 'oneview' | 'stactize' | 'surge'): Promise<void> => {
      const prior = documents.find((d) => d.filename === filename);
      if (!prior) return;
      const priorKind = (prior as DocumentRegistryEntry & { productKind?: string }).productKind;
      setDocuments((cur) =>
        cur.map((d) =>
          d.filename === filename ? ({ ...d, productKind: kind } as DocumentRegistryEntry) : d,
        ),
      );
      try {
        await apiPatch(`/api/v1/admin/knowledgebase/documents/${encodeURIComponent(filename)}`, {
          productKind: kind,
        });
      } catch (err) {
        setDocuments((cur) =>
          cur.map((d) =>
            d.filename === filename
              ? ({ ...d, productKind: priorKind } as DocumentRegistryEntry)
              : d,
          ),
        );
        if (err instanceof ApiError && err.code === 'ingest_lock_held') {
          setLockHeld(true);
        }
        setTopError(errorMessage(err, 'Could not update product kind.'));
      }
    },
    [documents],
  );

  /**
   * Per-row confidential-kind save. Confidential docs carry a sub-classification
   * (internal/vendor/client) used purely for display + filtering — same PATCH
   * route, optimistic update + rollback shape as patchMetadata.
   */
  const patchConfidentialKind = useCallback(
    async (filename: string, kind: 'internal' | 'vendor' | 'client'): Promise<void> => {
      const prior = documents.find((d) => d.filename === filename);
      if (!prior) return;
      const priorKind = (prior as DocumentRegistryEntry & { confidentialKind?: string })
        .confidentialKind;
      setDocuments((cur) =>
        cur.map((d) =>
          d.filename === filename ? ({ ...d, confidentialKind: kind } as DocumentRegistryEntry) : d,
        ),
      );
      try {
        await apiPatch(`/api/v1/admin/knowledgebase/documents/${encodeURIComponent(filename)}`, {
          confidentialKind: kind,
        });
      } catch (err) {
        setDocuments((cur) =>
          cur.map((d) =>
            d.filename === filename
              ? ({ ...d, confidentialKind: priorKind } as DocumentRegistryEntry)
              : d,
          ),
        );
        if (err instanceof ApiError && err.code === 'ingest_lock_held') {
          setLockHeld(true);
        }
        setTopError(errorMessage(err, 'Could not update confidential kind.'));
      }
    },
    [documents],
  );

  /**
   * Per-row specialisations save. Shape mirrors patchMetadata: optimistic
   * write, rollback on error, surface user-facing error via topError. The
   * same /documents/:filename PATCH endpoint accepts a specialisations
   * array; the backend handles audit + chunk propagation.
   */
  const patchSpecialisations = useCallback(
    async (filename: string, next: string[]): Promise<void> => {
      const prior = documents.find((d) => d.filename === filename);
      if (!prior) return;
      const priorSpecs = prior.specialisations ?? [];
      setSavingFilename(filename);
      setDocuments((cur) =>
        cur.map((d) => (d.filename === filename ? { ...d, specialisations: next } : d)),
      );
      try {
        await apiPatch(`/api/v1/admin/knowledgebase/documents/${encodeURIComponent(filename)}`, {
          specialisations: next,
        });
      } catch (err) {
        setDocuments((cur) =>
          cur.map((d) => (d.filename === filename ? { ...d, specialisations: priorSpecs } : d)),
        );
        if (err instanceof ApiError && err.code === 'ingest_lock_held') {
          setLockHeld(true);
        }
        setTopError(errorMessage(err, 'Could not save specialisations.'));
      } finally {
        setSavingFilename((cur) => (cur === filename ? null : cur));
      }
    },
    [documents],
  );

  // Memoised picker vocabularies per ring. Falls back to hardcoded
  // CANONICAL_* arrays inside InlineSpecialisationsCell if the fetch
  // hasn't resolved yet.
  const ring0Names = useMemo(
    () =>
      specialisations.length === 0
        ? undefined
        : specialisations.filter((s) => s.ring === 'ring0').map((s) => s.name),
    [specialisations],
  );
  const ring1Names = useMemo(
    () =>
      specialisations.length === 0
        ? undefined
        : specialisations.filter((s) => s.ring === 'ring1').map((s) => s.name),
    [specialisations],
  );

  // Sorted + filtered view of the corpus. Memoised so flipping a filter
  // chip doesn't re-walk the array on every keystroke elsewhere. The
  // page slice that actually renders is derived below.
  const sortedFiltered = useMemo(
    () =>
      sortDocuments(
        filterDocuments(
          documents,
          sourceKind,
          ringFilter,
          classificationFilter,
          confidentialKindFilter,
          productKindFilter,
          searchQ,
        ),
        sortBy,
        sortDir,
      ),
    [
      documents,
      sourceKind,
      ringFilter,
      classificationFilter,
      confidentialKindFilter,
      productKindFilter,
      searchQ,
      sortBy,
      sortDir,
    ],
  );
  const totalFiltered = sortedFiltered.length;
  const pageCount = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const pageClamped = Math.min(page, pageCount);
  const pagedDocs = useMemo(
    () => sortedFiltered.slice((pageClamped - 1) * pageSize, pageClamped * pageSize),
    [sortedFiltered, pageClamped, pageSize],
  );
  // Any change to the filter / sort axes drops the user back to page 1
  // so they don't end up looking at an empty page beyond the new total.
  useEffect(() => {
    setPage(1);
  }, [
    sourceKind,
    ringFilter,
    classificationFilter,
    confidentialKindFilter,
    productKindFilter,
    searchQ,
    sortBy,
    sortDir,
    pageSize,
  ]);

  /**
   * Resolve the specialisation vocabulary for a given doc. Confidential
   * docs are internal-flavoured (board / strategy / vendor / client
   * categories) and don't share product-doc tags like "WAN/LAN" or
   * "Service Desk" — so they draw from the Ring 0 vocab instead. Any new
   * confidential-specific names can be added via /admin/specialisations
   * with ring=ring0.
   */
  const namesForDoc = useCallback(
    (d: { ring?: string; classification?: string }): string[] | undefined => {
      if (
        d.ring === 'ring0' ||
        d.classification === 'confidential' ||
        d.classification === 'restricted'
      ) {
        return ring0Names;
      }
      return ring1Names;
    },
    [ring0Names, ring1Names],
  );

  const triggerReingest = async (filename: string): Promise<void> => {
    try {
      const res = await apiPost<{ jobId: string; status: string }>(
        `/api/v1/admin/knowledgebase/documents/${encodeURIComponent(filename)}/reingest`,
        {},
      );
      const id = `q_${Date.now()}_re`;
      setQueue((cur) => [
        ...cur,
        {
          id,
          filename,
          file: new File([], filename),
          stage: 'indexing',
          jobId: res.jobId,
          progressPct: 80,
          ring: 'ring1',
        },
      ]);
      await pollJob(id, res.jobId);
      setLockHeld(false);
      refresh();
    } catch (err) {
      setTopError(errorMessage(err));
    }
  };

  // ── KPI derivations ────────────────────────────────────────────────
  // All metrics derive from existing fetched state (`documents` registry +
  // local `queue`). No new API calls — Phase 4 constraint. If a value can't
  // be derived (e.g. no docs yet → no lastIngestedAt) we render "—".
  const kpis = useMemo(() => {
    const totalDocs = documents.length;
    const ring0Count = documents.filter((d) => d.ring === 'ring0').length;
    const ring1Count = totalDocs - ring0Count;
    const totalChunks = documents.reduce((sum, d) => sum + (d.chunkCount ?? 0), 0);
    // Most-recent successful ingest across the registry.
    let lastIngestedMs: number | null = null;
    for (const d of documents) {
      if (!d.lastIngestedAt) continue;
      const t = Date.parse(d.lastIngestedAt);
      if (Number.isFinite(t) && (lastIngestedMs === null || t > lastIngestedMs)) {
        lastIngestedMs = t;
      }
    }
    // Active jobs = queue items mid-flight. Matches the stages that the
    // upload pipeline + reingest path park items in before they reach
    // done/error/queued.
    const ACTIVE_STAGES: QueueStage[] = ['uploading', 'validating', 'waiting', 'indexing'];
    const activeJobs = queue.filter((q) => ACTIVE_STAGES.includes(q.stage)).length;
    return { totalDocs, ring0Count, ring1Count, totalChunks, lastIngestedMs, activeJobs };
  }, [documents, queue]);

  // Live-ingest banner trigger: first in-flight queue item drives the
  // "currently ingesting" string. filesProcessed counts done items in the
  // CURRENT queue window (cleared rows don't count). No `staleSince` is
  // available on this page's data shape, so the stale-warning variant is
  // skipped — flagged as a follow-up in the commit body.
  const activeItem = queue.find(
    (q) =>
      q.stage === 'uploading' ||
      q.stage === 'validating' ||
      q.stage === 'indexing' ||
      q.stage === 'waiting',
  );
  const showIngestBanner = activeItem !== undefined;
  const filesTotal = queue.length;
  const filesProcessed = queue.filter((q) => q.stage === 'done').length;

  return (
    <Layout>
      {/* Mobile gate — the upload + table flows aren't usable below ~768px
          and we don't expect Andre to admin from her phone. Surface a clear
          message instead of a broken layout. */}
      <div className="flex h-full items-center justify-center px-6 md:hidden">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold text-ink-900">Admin is only available on desktop</h1>
          <p className="mt-2 text-sm text-ink-700">
            Uploading and reindexing documents works best on a laptop or tablet in landscape mode.
            Please open this page again from a larger screen.
          </p>
        </div>
      </div>
      <div className="thin-scroll hidden h-full overflow-y-auto md:block">
        <div className="mx-auto max-w-6xl px-6 py-6">
          <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="ov-h-page">Knowledgebase Admin</h1>
              <p className="ov-body mt-1">
                Upload, reindex, or delete documents. Changes are visible immediately to all users
                of the Knowledge Agent.
              </p>
            </div>
            <button
              type="button"
              aria-pressed={showVersions}
              onClick={() => setShowVersions((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium ${
                showVersions
                  ? 'border-nebula-primary bg-nebula-primary text-white'
                  : 'border-nebula-border text-nebula-fg-2 hover:bg-nebula-tint'
              }`}
            >
              <Icon name="folder_copy" size={18} />
              Versions
            </button>
          </header>
          {showVersions && <DocumentVersionsPanel />}

          {/* ── KPI tiles ──────────────────────────────────────────── */}
          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiTile
              label="Total documents"
              value={kpis.totalDocs.toLocaleString('en-GB')}
              sub={`Ring 0: ${kpis.ring0Count.toLocaleString('en-GB')} · Ring 1: ${kpis.ring1Count.toLocaleString('en-GB')}`}
            />
            <KpiTile
              label="Total chunks"
              value={kpis.totalChunks > 0 ? kpis.totalChunks.toLocaleString('en-GB') : '—'}
              sub={
                kpis.totalDocs > 0
                  ? `${Math.round(kpis.totalChunks / Math.max(1, kpis.totalDocs)).toLocaleString('en-GB')} avg / doc`
                  : undefined
              }
            />
            <KpiTile
              label="Last sync"
              value={kpis.lastIngestedMs !== null ? relativeTime(kpis.lastIngestedMs) : '—'}
              sub={
                kpis.lastIngestedMs !== null
                  ? new Date(kpis.lastIngestedMs).toLocaleString('en-GB')
                  : undefined
              }
            />
            <KpiTile
              label="Active jobs"
              value={kpis.activeJobs.toLocaleString('en-GB')}
              sub={kpis.activeJobs > 0 ? 'Ingesting now' : 'Idle'}
            />
          </div>

          {/* ── Canonical facts overlay ─────────────────────────────── */}
          {/* ── Live-ingest banner ─────────────────────────────────── */}
          {showIngestBanner && activeItem && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-nebula-secondary/20 bg-nebula-tint px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="inline-flex h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-nebula-secondary" />
                <p className="ov-body min-w-0 truncate">
                  Ingesting{' '}
                  <span className="font-semibold text-nebula-fg-1">{activeItem.filename}</span> ·{' '}
                  {filesProcessed}/{filesTotal} files
                </p>
              </div>
              <button
                type="button"
                onClick={() => console.info('view-ingest-details')}
                className="ov-meta-upper flex-shrink-0 text-nebula-secondary hover:underline"
              >
                View details
              </button>
            </div>
          )}

          {topError && (
            <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-nebula-error/20 bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
              <span>{topError}</span>
              <button
                type="button"
                onClick={() => setTopError(null)}
                className="text-nebula-error/60 hover:text-nebula-error"
                aria-label="Close error message"
              >
                <XIcon size={14} />
              </button>
            </div>
          )}

          {lockHeld && (
            <div className="mb-4 flex items-center gap-2 rounded-md border border-nebula-warning/20 bg-nebula-warning-bg px-3 py-2 text-sm text-nebula-warning">
              <AlertTriangleIcon size={14} />
              <span>
                A document is being indexed right now, so metadata edits and deletes are refused
                until it finishes. Uploads and reindexes are queued and start automatically.
              </span>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-3">
            {/* Uploads queue server-side, so a running ingest no longer
                blocks the zones — only an open pre-flight does. */}
            <UploadZone
              disabled={bulk.preflight !== null}
              ring="ring0"
              title="Internal"
              description="Handbook, policies, ways-of-working — OneNebula employees only"
              onFiles={(items, folder, ring, classification, accessGroups) =>
                onZoneFiles(items, folder, ring, classification, accessGroups)
              }
            />
            <UploadZone
              disabled={bulk.preflight !== null}
              ring="ring1"
              showProductKind
              title="Product"
              description="OneView, Stactize, Surge — product features, customer-facing docs, runbooks. Pick the product below."
              onFiles={(items, folder, ring, classification, accessGroups, _ck, productKind) =>
                onZoneFiles(
                  items,
                  folder,
                  ring,
                  classification,
                  accessGroups,
                  undefined,
                  productKind,
                )
              }
            />
            <UploadZone
              disabled={bulk.preflight !== null}
              ring="ring0"
              variant="confidential"
              classification="confidential"
              accessGroups={['confidential']}
              showConfidentialKind
              title="Confidential"
              description="Client contracts, revenue proposals, board materials. Only members of KB-Confidential can retrieve these in chat."
              onFiles={(items, folder, ring, classification, accessGroups, confidentialKind) =>
                onZoneFiles(items, folder, ring, classification, accessGroups, confidentialKind)
              }
            />
          </div>

          <BatchProgressPanel
            localUploads={bulk.localUploads}
            describeError={(code) =>
              (code && ERROR_MESSAGES[code]) || 'Indexing failed. Upload the file again.'
            }
            keepAsDownloadCodes={KEEP_AS_DOWNLOAD_ELIGIBLE_CODES}
            onBatchSettled={refresh}
          />

          {queue.length > 0 && (
            <section className="mt-6 space-y-2">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="ov-h-section">Queue</h2>
                {queue.some((q) => q.stage === 'done' || q.stage === 'error') && (
                  <button
                    type="button"
                    onClick={() =>
                      setQueue((cur) =>
                        cur.filter((q) => q.stage !== 'done' && q.stage !== 'error'),
                      )
                    }
                    className="text-xs text-ink-700/60 hover:text-ink-900"
                  >
                    Clear completed / failed
                  </button>
                )}
              </div>
              {queue.map((q) => (
                <QueueRow
                  key={q.id}
                  item={q}
                  availableSpecialisations={namesForDoc({
                    ring: q.ring,
                    classification: q.classification,
                  })}
                  // Restored items have no File — retry would re-PUT
                  // nothing. Hide the retry button server-side; let the
                  // user dismiss and re-drop the file from disk.
                  canRetry={q.file !== undefined}
                  onRetry={() => {
                    if (!q.file) return;
                    updateQueueItem(q.id, {
                      stage: 'queued',
                      errorMessage: undefined,
                      errorCode: undefined,
                      progressPct: 0,
                      jobId: undefined,
                    });
                    // Route through the same single-flight chain as fresh
                    // drops. Direct processOne() calls let multiple clicks
                    // race the global ingest lock and dump red errors.
                    const retryItem = { ...q, stage: 'queued' as const, errorMessage: undefined };
                    queueChainRef.current = queueChainRef.current
                      .catch(() => undefined)
                      .then(() => processOne(retryItem, true))
                      .then(() => {
                        setLockHeld(false);
                        refresh();
                      });
                  }}
                  canKeepAsDownload={
                    !!q.errorCode && !!q.jobId && KEEP_AS_DOWNLOAD_ELIGIBLE_CODES.has(q.errorCode)
                  }
                  onKeepAsDownload={async (specialisations) => {
                    if (!q.jobId) return;
                    try {
                      await apiPost<{ jobId: string; status: string; filename: string }>(
                        `/api/v1/admin/knowledgebase/documents/${q.jobId}/keep-as-download`,
                        // Send the operator-picked specialisations alongside
                        // the keep-as-download flip so the practitioner-facing
                        // /documenten card shows the right chip rail without
                        // a second trip to the inline editor.
                        specialisations.length > 0 ? { specialisations } : {},
                      );
                      updateQueueItem(q.id, {
                        stage: 'done',
                        errorMessage: undefined,
                        errorCode: undefined,
                      });
                      refresh();
                    } catch (err) {
                      updateQueueItem(q.id, {
                        stage: 'error',
                        errorMessage: errorMessage(err),
                        errorCode: err instanceof ApiError ? err.code : undefined,
                      });
                    }
                  }}
                  onDismiss={() => setQueue((cur) => cur.filter((x) => x.id !== q.id))}
                  onCancel={() => cancelQueueItem(q.id)}
                />
              ))}
            </section>
          )}

          <section className="mt-8">
            {(() => {
              // Derived counts for the filter chip badges. Memoising not
              // worth it for ~5K entries.
              const visible = filterDocuments(
                documents,
                sourceKind,
                ringFilter,
                classificationFilter,
                confidentialKindFilter,
                productKindFilter,
                searchQ,
              );
              const counts = countBySourceKind(documents);
              // Classification counts are computed over the current
              // source-kind + ring scope (search-agnostic), so chip badges
              // reflect "how many docs in the visible scope carry this
              // classification" rather than a global tally.
              const classificationScope = filterDocuments(
                documents,
                sourceKind,
                ringFilter,
                'all',
                'all',
                'all',
                '',
              );
              // Sub-scope: every product doc (ring1 non-confidential) in
              // the current source/ring view. Drives the OneView /
              // Stactize / Surge chip counts.
              const productScope = classificationScope.filter(
                (d) => (d.ring ?? 'ring1') === 'ring1' && docClassification(d) !== 'confidential',
              );
              const productKindCounts = {
                all: productScope.length,
                oneview: productScope.filter((d) => docProductKind(d) === 'oneview').length,
                stactize: productScope.filter((d) => docProductKind(d) === 'stactize').length,
                surge: productScope.filter((d) => docProductKind(d) === 'surge').length,
              };
              // Sub-scope: all confidential docs in the current source/ring
              // view. Used to compute the per-kind chip counts so the
              // operator sees "12 vendor / 4 client / 3 internal" instead
              // of a global tally.
              const confidentialScope = classificationScope.filter(
                (d) => docClassification(d) === 'confidential',
              );
              const confKindCounts = {
                all: confidentialScope.length,
                internal: confidentialScope.filter((d) => docConfidentialKind(d) === 'internal')
                  .length,
                vendor: confidentialScope.filter((d) => docConfidentialKind(d) === 'vendor').length,
                client: confidentialScope.filter((d) => docConfidentialKind(d) === 'client').length,
              };
              const classCounts = countByClassification(classificationScope);
              return (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="ov-h-section">
                      Documents ({visible.length}
                      {visible.length !== documents.length ? ` of ${documents.length}` : ''})
                    </h2>
                    {loading && <span className="ov-meta">Loading…</span>}
                  </div>

                  {/* Filter rail */}
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <SourceFilterChip
                      label="Uploads"
                      hint="PDFs, DOCX, spreadsheets you uploaded here"
                      count={counts.uploads}
                      active={sourceKind === 'uploads'}
                      onClick={() => setSourceKind('uploads')}
                    />
                    <SourceFilterChip
                      label="Confluence"
                      hint="Pages pulled in via the Confluence sync"
                      count={counts.confluence}
                      active={sourceKind === 'confluence'}
                      onClick={() => setSourceKind('confluence')}
                    />
                    <SourceFilterChip
                      label="Code"
                      hint="Files ingested by the Code Sources sync"
                      count={counts.code}
                      active={sourceKind === 'code'}
                      onClick={() => setSourceKind('code')}
                    />
                    <SourceFilterChip
                      label="All"
                      hint="Show every registry entry, regardless of source"
                      count={documents.length}
                      active={sourceKind === 'all'}
                      onClick={() => setSourceKind('all')}
                    />

                    <span className="mx-2 h-5 w-px bg-nebula-border-soft" />

                    <RingFilterChip
                      label="Both rings"
                      active={ringFilter === 'all'}
                      onClick={() => setRingFilter('all')}
                    />
                    <RingFilterChip
                      label="Ring 1 · Product"
                      active={ringFilter === 'ring1'}
                      onClick={() => setRingFilter('ring1')}
                    />
                    <RingFilterChip
                      label="Ring 0 · Internal"
                      active={ringFilter === 'ring0'}
                      onClick={() => setRingFilter('ring0')}
                    />

                    <span className="mx-2 h-5 w-px bg-nebula-border-soft" />

                    <SourceFilterChip
                      label="All classifications"
                      hint="Show docs at every ISO 27001 classification tier"
                      count={classificationScope.length}
                      active={classificationFilter === 'all'}
                      onClick={() => setClassificationFilter('all')}
                    />
                    <SourceFilterChip
                      label="Public"
                      hint="Public — no access restriction"
                      count={classCounts.public}
                      active={classificationFilter === 'public'}
                      onClick={() => setClassificationFilter('public')}
                    />
                    <SourceFilterChip
                      label="Internal"
                      hint="Internal — OneNebula staff only (default if unset)"
                      count={classCounts.internal}
                      active={classificationFilter === 'internal'}
                      onClick={() => setClassificationFilter('internal')}
                    />
                    <SourceFilterChip
                      label="Confidential"
                      hint="Confidential — need-to-know"
                      count={classCounts.confidential}
                      active={classificationFilter === 'confidential'}
                      onClick={() => setClassificationFilter('confidential')}
                    />
                    <SourceFilterChip
                      label="Restricted"
                      hint="Restricted — strictly controlled access"
                      count={classCounts.restricted}
                      active={classificationFilter === 'restricted'}
                      onClick={() => setClassificationFilter('restricted')}
                    />

                    {classificationFilter === 'confidential' && (
                      <>
                        <span className="mx-2 h-5 w-px bg-nebula-border-soft" />
                        <SourceFilterChip
                          label="All confidential"
                          hint="Every confidential doc regardless of kind"
                          count={confKindCounts.all}
                          active={confidentialKindFilter === 'all'}
                          onClick={() => setConfidentialKindFilter('all')}
                        />
                        <SourceFilterChip
                          label="Confidential · Internal"
                          hint="OneNebula's own confidential material (board, strategy)"
                          count={confKindCounts.internal}
                          active={confidentialKindFilter === 'internal'}
                          onClick={() => setConfidentialKindFilter('internal')}
                        />
                        <SourceFilterChip
                          label="Confidential · Vendor"
                          hint="Supplier / vendor contracts and proposals"
                          count={confKindCounts.vendor}
                          active={confidentialKindFilter === 'vendor'}
                          onClick={() => setConfidentialKindFilter('vendor')}
                        />
                        <SourceFilterChip
                          label="Confidential · Client"
                          hint="Customer contracts, proposals, NDAs"
                          count={confKindCounts.client}
                          active={confidentialKindFilter === 'client'}
                          onClick={() => setConfidentialKindFilter('client')}
                        />
                      </>
                    )}

                    {(classificationFilter === 'all' ||
                      classificationFilter === 'internal' ||
                      classificationFilter === 'public') && (
                      <>
                        <span className="mx-2 h-5 w-px bg-nebula-border-soft" />
                        <SourceFilterChip
                          label="All products"
                          hint="Every product doc (ring1, non-confidential) regardless of line"
                          count={productKindCounts.all}
                          active={productKindFilter === 'all'}
                          onClick={() => setProductKindFilter('all')}
                        />
                        <SourceFilterChip
                          label="OneView"
                          hint="OneView product documentation"
                          count={productKindCounts.oneview}
                          active={productKindFilter === 'oneview'}
                          onClick={() => setProductKindFilter('oneview')}
                        />
                        <SourceFilterChip
                          label="Stactize"
                          hint="Stactize product documentation"
                          count={productKindCounts.stactize}
                          active={productKindFilter === 'stactize'}
                          onClick={() => setProductKindFilter('stactize')}
                        />
                        <SourceFilterChip
                          label="Surge"
                          hint="Surge product documentation"
                          count={productKindCounts.surge}
                          active={productKindFilter === 'surge'}
                          onClick={() => setProductKindFilter('surge')}
                        />
                      </>
                    )}

                    <div className="ml-auto flex w-full items-center gap-2 rounded-md border border-nebula-border-soft bg-nebula-app px-2.5 py-1.5 sm:w-72">
                      <span
                        className="material-symbols-outlined text-nebula-fg-4"
                        style={{ fontSize: 14 }}
                      >
                        search
                      </span>
                      <input
                        type="text"
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        placeholder="Search by title or filename…"
                        className="flex-1 bg-transparent text-sm text-nebula-fg-1 outline-none placeholder:text-nebula-fg-4"
                      />
                      {searchQ && (
                        <button
                          type="button"
                          onClick={() => setSearchQ('')}
                          aria-label="Clear search"
                          className="text-nebula-fg-4 hover:text-nebula-fg-1"
                        >
                          <XIcon size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
            {/* Responsive layout (QA 2026-06-10): the fixed side columns used
                to claim 848px unconditionally, so on narrower viewports the
                flexible File column collapsed to ~0 — titles invisible,
                headers overlapping. Two-part fix: (1) the side columns shrink
                below xl so the name keeps the lion's share; (2) the table has
                a hard min-width inside overflow-x-auto, so below that floor
                the card scrolls horizontally instead of crushing the first
                column. (The earlier left-edge clipping that got
                overflow-x-auto removed came from auto table layout growing
                past the card; with table-fixed + an explicit min-width the
                table starts at scrollLeft 0 and can't clip the File column.)
                Long filenames still truncate in-cell via min-w-0 + truncate;
                the action column keeps whitespace-nowrap. */}
            <div className="overflow-x-auto rounded-md border border-nebula-border-soft bg-nebula-surface shadow-card">
              <table className="w-full min-w-[860px] table-fixed text-sm">
                <colgroup>
                  <col />
                  <col className="w-36 xl:w-48" />
                  <col className="w-20 xl:w-24" />
                  <col className="w-32 xl:w-40" />
                  <col className="w-40 xl:w-56" />
                  <col className="w-36 xl:w-44" />
                </colgroup>
                <thead className="bg-nebula-gray-5 text-left text-[11px] uppercase tracking-wider text-nebula-fg-3">
                  <tr>
                    <SortableTh
                      label="File"
                      column="filename"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                    />
                    <SortableTh
                      label="Author"
                      column="author"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                    />
                    <SortableTh
                      label="Chunks"
                      column="chunkCount"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                    />
                    <SortableTh
                      label="Last indexed"
                      column="lastIngestedAt"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                    />
                    <th className="px-4 py-3 font-medium">Specialisations</th>
                    <th className="py-3 pl-4 pr-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-50">
                  {pagedDocs.map((d) => (
                    <tr key={d.filename} className="hover:bg-brand-50/40">
                      <td className="min-w-0 px-4 py-3 font-medium text-ink-900">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => void openDocument(d.filename, setTopError)}
                            title={d.filename}
                            className="inline-flex min-w-0 flex-1 items-center gap-2 text-left text-ink-900 hover:text-brand-700 hover:underline"
                          >
                            <FileTextIcon
                              size={14}
                              className="flex-shrink-0 text-brand-700/60"
                              aria-hidden="true"
                            />
                            <span className="truncate">
                              {d.documentTitle && d.documentTitle !== d.filename
                                ? d.documentTitle
                                : d.filename}
                            </span>
                          </button>
                          <ScopeChip
                            doc={d}
                            disabled={lockHeld}
                            onChangeKind={(kind) => patchConfidentialKind(d.filename, kind)}
                            onChangeProductKind={(kind) => patchProductKind(d.filename, kind)}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-ink-900/70">
                        <EditableCell
                          value={d.author}
                          placeholder="Unknown"
                          ariaLabel={`Author of ${d.filename}`}
                          disabled={lockHeld}
                          required
                          onSave={(v) => patchMetadata(d.filename, 'author', v)}
                        />
                      </td>
                      <td className="px-4 py-3 text-ink-900/70">
                        {d.documentOnly ? (
                          <span
                            title="Available as download only — the assistant does not search this document."
                            className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
                          >
                            Download only
                          </span>
                        ) : d.chunkCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => setInspectChunks(d)}
                            title="View chunks"
                            className="rounded-md px-2 py-0.5 font-medium text-brand-700 hover:bg-brand-50 hover:underline"
                          >
                            {d.chunkCount}
                          </button>
                        ) : (
                          <span>{d.chunkCount}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-900/70">
                        {d.lastIngestedAt
                          ? new Date(d.lastIngestedAt).toLocaleString('en-GB')
                          : '—'}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <InlineSpecialisationsCell
                          value={d.specialisations ?? []}
                          saving={savingFilename === d.filename || lockHeld}
                          onSave={(v) => patchSpecialisations(d.filename, v)}
                          maxItems={10}
                          ring={d.ring}
                          availableNames={namesForDoc(d)}
                        />
                      </td>
                      {/* pr-6 (24px) instead of px-4 on the actions column —
                          the right-aligned trash button looked flush against
                          the card edge with the default 16px padding. */}
                      <td className="whitespace-nowrap py-3 pl-4 pr-8 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            type="button"
                            disabled={lockHeld}
                            onClick={() => void triggerReingest(d.filename)}
                            className="inline-flex items-center gap-1 rounded-md border border-brand-100 px-2 py-1 text-xs text-ink-800 hover:bg-brand-50 disabled:opacity-50"
                          >
                            <RefreshCwIcon size={12} /> Reindex
                          </button>
                          <button
                            type="button"
                            disabled={lockHeld}
                            onClick={() => setPendingDelete(d)}
                            aria-label={`Delete ${d.filename}`}
                            title="Delete"
                            className="inline-flex items-center justify-center rounded-md border border-red-100 bg-red-50 p-1.5 text-red-700 hover:bg-red-100 disabled:opacity-50"
                          >
                            <Trash2Icon size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {documents.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-ink-700/60">
                        No documents in the knowledgebase. Upload the first document above.
                      </td>
                    </tr>
                  )}
                  {documents.length > 0 && totalFiltered === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-ink-700/60">
                        No documents match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {totalFiltered > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-brand-50 bg-brand-50/30 px-4 py-2.5 text-xs text-ink-700">
                  <div>
                    Showing{' '}
                    <strong className="font-semibold text-ink-900">
                      {(pageClamped - 1) * pageSize + 1}–
                      {Math.min(pageClamped * pageSize, totalFiltered)}
                    </strong>{' '}
                    of <strong className="font-semibold text-ink-900">{totalFiltered}</strong>
                    {totalFiltered !== documents.length && (
                      <span className="text-ink-700/60"> (filtered from {documents.length})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5">
                      <span className="text-ink-700/70">Rows per page</span>
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value) as PageSizeOpt)}
                        className="rounded border border-brand-100 bg-white px-1.5 py-0.5 text-xs"
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={pageClamped <= 1}
                        className="rounded border border-brand-100 bg-white px-2 py-0.5 text-ink-900 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <span className="font-semibold text-ink-900">
                        Page {pageClamped} of {pageCount}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        disabled={pageClamped >= pageCount}
                        className="rounded border border-brand-100 bg-white px-2 py-0.5 text-ink-900 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {bulk.preflight && (
        <BulkUploadPreflight
          state={bulk.preflight}
          onCancel={bulk.cancel}
          onConfirm={(choices) => void bulk.confirm(choices)}
        />
      )}

      {collision && (
        <CollisionModal
          files={collision.files}
          onCancel={() => {
            collision.resolve(false);
            setCollision(null);
          }}
          onConfirm={() => {
            collision.resolve(true);
            setCollision(null);
          }}
        />
      )}

      {pendingDelete && (
        <DeleteModal
          doc={pendingDelete}
          onClose={() => setPendingDelete(null)}
          onDeleted={() => {
            setPendingDelete(null);
            refresh();
          }}
          onError={(msg) => setTopError(msg)}
        />
      )}
      {inspectChunks && <ChunksModal doc={inspectChunks} onClose={() => setInspectChunks(null)} />}
    </Layout>
  );
}

type ConfidentialKind = 'internal' | 'vendor' | 'client';
type ProductKind = 'oneview' | 'stactize' | 'surge';

interface UploadZoneProps {
  disabled: boolean;
  /** Tier this zone routes uploads to. Internal → Ring 0 (RBAC-gated to
   *  OneNebula employees). Product → Ring 1 (broader access, customer-
   *  facing docs). Each zone has its own drag-over state so the user
   *  sees which one accepted the drop. */
  ring: 'ring0' | 'ring1';
  /** Headline above the dropbox (e.g. "Internal" / "Product"). */
  title: string;
  /** Subline describing what belongs in this zone. */
  description: string;
  /** Visual accent: brand / mosterdgeel / confidential. Defaults map from `ring`. */
  variant?: 'internal' | 'product' | 'confidential';
  /** ISO 27001 classification cascaded onto every upload via this zone.
   *  When set to `confidential`/`restricted` the zone hard-locks
   *  `accessGroups` to ['confidential']; downgrades require an explicit
   *  PATCH later. */
  classification?: 'public' | 'internal' | 'confidential' | 'restricted';
  accessGroups?: string[];
  /** When true, the zone renders a 3-segment Internal/Vendor/Client toggle
   *  above the dropbox and forwards the chosen kind on every drop. */
  showConfidentialKind?: boolean;
  /** When true, the zone renders a 3-segment OneView/Stactize/Surge toggle
   *  above the dropbox and forwards the chosen product kind on every drop. */
  showProductKind?: boolean;
  onFiles: (
    items: DroppedFile[],
    /** True when the drop / pick contained a folder (bulk flow). */
    folder: boolean,
    ring: 'ring0' | 'ring1',
    classification?: 'public' | 'internal' | 'confidential' | 'restricted',
    accessGroups?: string[],
    confidentialKind?: ConfidentialKind,
    productKind?: ProductKind,
  ) => void;
}

function UploadZone({
  disabled,
  ring,
  title,
  description,
  variant,
  classification,
  accessGroups,
  showConfidentialKind,
  showProductKind,
  onFiles,
}: UploadZoneProps): JSX.Element {
  const dragCounter = useRef(0);
  const [dragOver, setDragOver] = useState(false);
  const [kind, setKind] = useState<ConfidentialKind>('internal');
  const [productKind, setProductKind] = useState<ProductKind>('oneview');

  const onDragEnter = (e: React.DragEvent): void => {
    e.preventDefault();
    dragCounter.current += 1;
    setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragOver(false);
    }
  };
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  // Send picked files up to the parent. OS housekeeping files (.DS_Store,
  // Thumbs.db, desktop.ini, other dotfiles) are dropped silently — listing
  // them as "skipped" in the pre-flight would only be noise. Everything else,
  // supported or not, goes to the parent so skips are reported with reasons.
  const dispatch = (items: DroppedFile[], folder: boolean): void => {
    const picked = items.filter((i) => !isOsNoiseFile(i.file.name));
    if (picked.length === 0) return;
    onFiles(
      picked,
      folder,
      ring,
      classification,
      accessGroups,
      showConfidentialKind ? kind : undefined,
      showProductKind ? productKind : undefined,
    );
  };

  const onDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOver(false);
    if (disabled) return;
    const dt = e.dataTransfer;
    const flat = Array.from(dt.files);
    // webkitGetAsEntry must be called synchronously while the event is live.
    const entries = (dt.items ? Array.from(dt.items) : [])
      .map((it) => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null))
      .filter((en): en is FileSystemEntry => en != null);
    if (entries.length === 0 || !entries.some((en) => en.isDirectory)) {
      dispatch(
        flat.map((f) => ({ file: f, relativePath: f.name })),
        false,
      );
      return;
    }
    void Promise.all(entries.map(entryToFiles)).then((nested) => dispatch(nested.flat(), true));
  };
  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files) return;
    dispatch(
      Array.from(e.target.files).map((f) => ({ file: f, relativePath: f.name })),
      false,
    );
    e.target.value = '';
  };
  // The directory picker (webkitdirectory) hands back every file in the chosen
  // folder + subfolders, each with its path in webkitRelativePath.
  const onFolderInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files) return;
    dispatch(
      Array.from(e.target.files).map((f) => ({
        file: f,
        relativePath: f.webkitRelativePath || f.name,
      })),
      true,
    );
    e.target.value = '';
  };

  // Internal zone gets a slightly warmer accent (mosterdgeel) so it's
  // visually distinct from Product (brand/Navy) — small affordance for
  // "this is the sensitive lane". Full class strings (not interpolated)
  // so Tailwind's JIT picks them up at build time.
  const resolvedVariant =
    variant ??
    (classification === 'confidential' || classification === 'restricted'
      ? 'confidential'
      : ring === 'ring0'
        ? 'internal'
        : 'product');
  const cls =
    resolvedVariant === 'internal'
      ? {
          border: dragOver
            ? 'border-mosterdgeel-500 bg-mosterdgeel-50'
            : 'border-mosterdgeel-200 bg-white hover:border-mosterdgeel-400',
          icon: 'text-mosterdgeel-700',
        }
      : resolvedVariant === 'confidential'
        ? {
            // Petrol accent — read as "elevated trust" against the brand
            // and mosterdgeel cards. Matches the Restricted citation chip.
            border: dragOver
              ? 'border-petrol-500 bg-petrol-50'
              : 'border-petrol-200 bg-white hover:border-petrol-400',
            icon: 'text-petrol-700',
          }
        : {
            border: dragOver
              ? 'border-brand-500 bg-brand-50'
              : 'border-brand-200 bg-white hover:border-brand-400',
            icon: 'text-brand-700',
          };

  return (
    <label
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
        disabled ? 'cursor-not-allowed border-ink-200 bg-ink-50 opacity-60' : cls.border
      }`}
    >
      <div className="flex items-center gap-2">
        <UploadCloudIcon size={22} className={cls.icon} />
        <span className="text-sm font-semibold text-ink-900">{title}</span>
      </div>
      <span className="text-xs text-ink-700/70">{description}</span>
      <span className="text-[11px] text-ink-700/50">
        Drop files or a folder here, click to choose files, or{' '}
        <button
          type="button"
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            folderInputRef.current?.click();
          }}
          className="underline hover:text-ink-900 disabled:no-underline"
        >
          choose a folder
        </button>{' '}
        · PDF, DOCX, PPT(X), ODT, XLS(X), VSD(X), images, JSON, CSV · max 100 MB. Folders upload
        every supported file inside; you review a summary first, and indexing continues on the
        server once the files are uploaded.
      </span>
      {showConfidentialKind && (
        <div
          className="mt-2 inline-flex overflow-hidden rounded-md border border-petrol-300 bg-white text-[11px]"
          onClick={(e) => e.preventDefault()}
        >
          {(['internal', 'vendor', 'client'] as const).map((k, i) => {
            const active = k === kind;
            const label = k === 'internal' ? 'Internal' : k === 'vendor' ? 'Vendor' : 'Client';
            return (
              <button
                key={k}
                type="button"
                disabled={disabled}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setKind(k);
                }}
                className={[
                  'px-3 py-1 font-medium transition-colors',
                  i > 0 ? 'border-l border-petrol-300' : '',
                  active
                    ? 'bg-petrol-600 text-white'
                    : 'bg-white text-petrol-800 hover:bg-petrol-50',
                ].join(' ')}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
      {showConfidentialKind && (
        <span className="text-[10px] text-ink-700/60">
          Internal = OneNebula's own (board, strategy). Vendor = supplier contracts. Client =
          customer contracts and proposals.
        </span>
      )}
      {showProductKind && (
        <div
          className="mt-2 inline-flex overflow-hidden rounded-md border border-brand-300 bg-white text-[11px]"
          onClick={(e) => e.preventDefault()}
        >
          {(['oneview', 'stactize', 'surge'] as const).map((k, i) => {
            const active = k === productKind;
            const label = k === 'oneview' ? 'OneView' : k === 'stactize' ? 'Stactize' : 'Surge';
            return (
              <button
                key={k}
                type="button"
                disabled={disabled}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setProductKind(k);
                }}
                className={[
                  'px-3 py-1 font-medium transition-colors',
                  i > 0 ? 'border-l border-brand-300' : '',
                  active ? 'bg-brand-600 text-white' : 'bg-white text-brand-800 hover:bg-brand-50',
                ].join(' ')}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
      {showProductKind && (
        <span className="text-[10px] text-ink-700/60">
          Pick the OneNebula product line this doc relates to. Inline-editable later via the chip.
        </span>
      )}
      <input
        type="file"
        multiple
        accept=".pdf,.docx,.doc,.pptx,.ppt,.odt,.xlsx,.xls,.ods,.jpg,.jpeg,.jfif,.png,.webp,.gif,.vsdx,.vsd,.json,.csv"
        disabled={disabled}
        onChange={onFileInput}
        className="hidden"
      />
      <input
        ref={(el) => {
          folderInputRef.current = el;
          // webkitdirectory isn't in React's typed attribute set; set it on the
          // node so the picker selects a directory (and recurses into it).
          if (el) {
            el.setAttribute('webkitdirectory', '');
            el.setAttribute('directory', '');
          }
        }}
        type="file"
        multiple
        disabled={disabled}
        onChange={onFolderInput}
        className="hidden"
      />
    </label>
  );
}

function QueueRow({
  item,
  onRetry,
  onDismiss,
  onCancel,
  canRetry,
  canKeepAsDownload,
  onKeepAsDownload,
  availableSpecialisations,
}: {
  item: QueueItem;
  onRetry: () => void;
  onDismiss: () => void;
  /** Pre-processing cancel — visible for queued/uploading/waiting stages
   *  so the operator can pull back a wrong-file drop before the Cloud Run
   *  ingest job fires. Validating/indexing aren't covered because the
   *  backend has already started work. */
  onCancel: () => void;
  /** Falls back to true for non-restored items. False when the underlying
   *  File reference is gone (queue rehydrated from sessionStorage). */
  canRetry: boolean;
  /** True when the failure is in the eligible-error-codes set AND we have
   *  a jobId to send to /keep-as-download. False otherwise (transient
   *  failures like rate_limited shouldn't tempt the operator into
   *  hiding them as download-only). */
  canKeepAsDownload: boolean;
  /** Operator-picked specialisations applied to the registry entry on
   *  keep-as-download. Empty array = no tags (matches pre-fix behaviour
   *  for users who skip the picker). */
  onKeepAsDownload: (specialisations: string[]) => void | Promise<void>;
  /** Ring-scoped vocabulary from the live admin-managed specialisations.
   *  Undefined while the fetch is in flight; the picker falls back to
   *  CANONICAL_SPECIALISATIONS (ring 0) only then. */
  availableSpecialisations?: string[];
}): JSX.Element {
  // Picker state: null = collapsed (button visible), array = expanded form
  // (chip rail + Save/Cancel). Capped at 10 picks (raised from SUPPOHRT's 3
  // for OneNebula's broader-spanning docs like the Employee Handbook).
  const [pickedSpecs, setPickedSpecs] = useState<string[] | null>(null);
  const [savingKeep, setSavingKeep] = useState(false);
  const togglePick = (s: string) =>
    setPickedSpecs((cur) => {
      if (!cur) return cur;
      if (cur.includes(s)) return cur.filter((x) => x !== s);
      if (cur.length >= 10) return cur; // cap at 10 — silent ignore once full
      return [...cur, s];
    });
  const stageLabel: Record<QueueStage, string> = {
    queued: 'In queue',
    uploading: 'Uploading',
    validating: 'Validating',
    // Server-side queue: the file is uploaded and waits its turn.
    waiting: 'Queued on the server',
    indexing: 'Indexing',
    done: 'Done',
    error: 'Error',
  };
  const isError = item.stage === 'error';
  const isDone = item.stage === 'done';
  const isWaiting = item.stage === 'waiting';
  const isPickerOpen = pickedSpecs !== null;
  // Stages where we can still pull the file back before the Cloud Run job
  // runs. validating/waiting/indexing are intentionally excluded — the file
  // is already on the server (waiting = in the server-side queue); the
  // operator should use the registry-row delete once it is indexed.
  const canCancel = item.stage === 'queued' || item.stage === 'uploading';
  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border px-3 py-2 text-sm ${
        isError
          ? 'border-red-100 bg-red-50'
          : isDone
            ? 'border-emerald-100 bg-emerald-50'
            : isWaiting
              ? 'border-amber-100 bg-amber-50'
              : 'border-brand-100 bg-white'
      }`}
    >
      <div className="flex items-center gap-3">
        {isDone ? (
          <CheckCircle2Icon size={16} className="text-emerald-600" />
        ) : isError ? (
          <XIcon size={16} className="text-red-700" />
        ) : isWaiting ? (
          <RefreshCwIcon size={16} className="animate-spin text-amber-700" />
        ) : (
          <FileTextIcon size={16} className="text-brand-700/60" />
        )}
        <div className="flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-medium text-ink-900">{item.filename}</span>
            <span className="text-xs text-ink-700/60">{stageLabel[item.stage]}</span>
          </div>
          {!isError && !isDone && (
            <div
              className={`mt-1 h-1 overflow-hidden rounded-full ${isWaiting ? 'bg-amber-100' : 'bg-brand-50'}`}
            >
              <div
                className={`h-full transition-all ${isWaiting ? 'bg-amber-500' : 'bg-brand-600'}`}
                style={{ width: `${item.progressPct ?? 0}%` }}
              />
            </div>
          )}
          {isError && item.errorMessage && (
            <p className="mt-1 text-xs text-red-700">{item.errorMessage}</p>
          )}
        </div>
        {canCancel && (
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel upload"
            title={
              item.stage === 'queued'
                ? 'Remove from queue'
                : item.stage === 'uploading'
                  ? 'Cancel upload'
                  : 'Cancel — stop waiting for the lock'
            }
            className={`inline-flex items-center justify-center rounded-md p-1 ${
              isWaiting
                ? 'text-amber-700/60 hover:bg-amber-100 hover:text-amber-700'
                : 'text-ink-700/50 hover:bg-ink-50 hover:text-ink-900'
            }`}
          >
            <XIcon size={14} />
          </button>
        )}
        {isError && (
          <div className="flex items-center gap-1">
            {canKeepAsDownload && pickedSpecs === null && (
              <button
                type="button"
                onClick={() => setPickedSpecs([])}
                title="Keep the file as download only — the assistant will not search it."
                className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-white px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
              >
                <FileTextIcon size={12} /> Download only
              </button>
            )}
            {canRetry && (
              <button
                type="button"
                onClick={onRetry}
                title={
                  item.errorCode === 'filename_collision'
                    ? 'Replace existing file and retry'
                    : 'Try again'
                }
                className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
              >
                <RefreshCwIcon size={12} />{' '}
                {item.errorCode === 'filename_collision' ? 'Overwrite & retry' : 'Try again'}
              </button>
            )}
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Close"
              title="Close"
              className="inline-flex items-center justify-center rounded-md p-1 text-red-700/60 hover:bg-red-100 hover:text-red-700"
            >
              <XIcon size={14} />
            </button>
          </div>
        )}
        {isDone && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            title="Close"
            className="inline-flex items-center justify-center rounded-md p-1 text-emerald-700/60 hover:bg-emerald-100 hover:text-emerald-700"
          >
            <XIcon size={14} />
          </button>
        )}
      </div>
      {/* Specialisation picker — appears inline when operator clicks
         "Download only". Forces the tags BEFORE the keep-as-download POST
         so the practitioner-facing card shows the chip rail immediately,
         without a second trip to /documenten. Picker stays optional —
         "Bewaar zonder tags" submits with an empty array. */}
      {isPickerOpen && (
        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-2">
          <p className="mb-2 text-xs font-medium text-amber-900">
            Which specialisations apply to this document? (max 10)
          </p>
          <div className="mb-2 flex flex-wrap gap-1">
            {(availableSpecialisations && availableSpecialisations.length > 0
              ? availableSpecialisations
              : CANONICAL_SPECIALISATIONS
            ).map((s) => {
              const picked = (pickedSpecs ?? []).includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => togglePick(s)}
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                    picked
                      ? 'border-brand-600 bg-brand-600 text-white'
                      : 'border-brand-200 bg-white text-ink-800 hover:bg-brand-50'
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setPickedSpecs(null)}
              disabled={savingKeep}
              className="rounded-md border border-brand-200 bg-white px-2 py-1 text-xs text-ink-800 hover:bg-brand-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                const specs = pickedSpecs ?? [];
                setSavingKeep(true);
                try {
                  await onKeepAsDownload(specs);
                  setPickedSpecs(null);
                } finally {
                  setSavingKeep(false);
                }
              }}
              disabled={savingKeep}
              className="rounded-md border border-brand-600 bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {savingKeep
                ? 'Saving…'
                : (pickedSpecs?.length ?? 0) === 0
                  ? 'Save without tags'
                  : `Save (${pickedSpecs?.length})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface CollisionModalProps {
  files: File[];
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Slice 6 commit 4 — single-cell inline-edit affordance.
 *
 * Click → input field with current value pre-filled. Enter saves;
 * Escape cancels; blur saves silently if the value changed (matches the
 * spec from the office-hours session). Optimistic update lives on the
 * parent, which writes through to the backend via patchMetadata; this
 * component just handles the local input state + keyboard semantics.
 */
interface EditableCellProps {
  value: string | undefined;
  placeholder: string;
  ariaLabel: string;
  disabled?: boolean;
  required?: boolean;
  onSave: (v: string) => Promise<void>;
}

function EditableCell({
  value,
  placeholder,
  ariaLabel,
  disabled,
  required,
  onSave,
}: EditableCellProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Re-sync draft if upstream value changes while we're not editing
  // (e.g. parent's optimistic update completes server confirmation).
  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  const startEdit = (): void => {
    if (disabled || saving) return;
    setEditing(true);
  };

  const commit = async (next: string): Promise<void> => {
    const cleaned = next.trim();
    // Required-field rule: don't accept empty submission for required fields.
    // Treat empty as cancel.
    if (required && cleaned === '') {
      setDraft(value ?? '');
      setEditing(false);
      return;
    }
    if (cleaned === (value ?? '').trim()) {
      // No change — quiet exit.
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(cleaned);
      setEditing(false);
    } catch {
      // Parent rolled back + surfaced the error already.
      setDraft(value ?? '');
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commit(draft);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(value ?? '');
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        disabled={saving}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit(draft)}
        onKeyDown={onKeyDown}
        className="w-full rounded border border-brand-300 bg-white px-1.5 py-0.5 text-sm focus:border-brand-500 focus:outline-none disabled:bg-brand-50"
      />
    );
  }

  const displayValue = value && value.trim() ? value : placeholder;
  const isPlaceholder = !value || !value.trim();

  return (
    <button
      type="button"
      onClick={startEdit}
      disabled={disabled}
      aria-label={`Edit ${ariaLabel}`}
      className={`group flex w-full items-start gap-1.5 rounded px-1 py-0.5 text-left text-sm transition-colors hover:bg-brand-50/60 disabled:cursor-not-allowed disabled:opacity-50 ${
        isPlaceholder ? 'italic text-ink-700/50' : 'text-ink-900/70'
      }`}
    >
      <span className="min-w-0 flex-1 break-all">{displayValue}</span>
      <PencilIcon
        size={11}
        className="mt-1 shrink-0 text-brand-700/40 opacity-0 group-hover:opacity-100"
      />
    </button>
  );
}

function CollisionModal({ files, onCancel, onConfirm }: CollisionModalProps): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-2 text-lg font-bold text-ink-950">File already exists</h3>
        <p className="mb-3 text-sm text-ink-700">
          The following files already exist in the knowledgebase. Choose <strong>Overwrite</strong>{' '}
          to replace them, or <strong>Skip</strong> to skip just these duplicates and continue with
          the rest.
        </p>
        <ul className="mb-4 max-h-40 overflow-y-auto rounded-md bg-ink-50 p-2 text-xs text-ink-800">
          {files.map((f) => (
            <li key={f.name} className="py-0.5">
              {f.name}
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-brand-100 px-3 py-1.5 text-sm text-ink-800 hover:bg-brand-50"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
          >
            Overwrite
          </button>
        </div>
      </div>
    </div>
  );
}

interface DeleteModalProps {
  doc: DocumentRegistryEntry;
  onClose: () => void;
  onDeleted: () => void;
  onError: (msg: string) => void;
}

function DeleteModal({ doc, onClose, onDeleted, onError }: DeleteModalProps): JSX.Element {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const matches = typed === doc.filename;

  const submit = async (): Promise<void> => {
    if (!matches) return;
    setSubmitting(true);
    try {
      await apiDelete(`/api/v1/admin/knowledgebase/documents/${encodeURIComponent(doc.filename)}`, {
        confirmFilename: doc.filename,
      });
      onDeleted();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-2 text-lg font-bold text-red-700">Delete document</h3>
        <p className="mb-2 text-sm text-ink-700">
          This will delete <strong>{doc.chunkCount}</strong>{' '}
          {doc.chunkCount === 1 ? 'chunk' : 'chunks'} from the Knowledge Agent. Deletion is
          permanent — a re-upload of the same file would have to be reindexed.
        </p>
        <p className="mb-2 text-sm text-ink-700">
          To confirm, type the filename exactly:{' '}
          <code className="rounded bg-ink-100 px-1 py-0.5 text-xs">{doc.filename}</code>
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={doc.filename}
          className="mb-4 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-brand-100 px-3 py-1.5 text-sm text-ink-800 hover:bg-brand-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!matches || submitting}
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:bg-red-300"
          >
            {submitting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- KPI tile + relative-time helpers --------------------------------

/**
 * Phase 4 OneView KPI tile — inline, single-use. Stays in this file by
 * design; Phase 4 brief is "keep blast radius tiny, no new files".
 */
function KpiTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-nebula-border-soft bg-nebula-surface p-4 transition-shadow hover:shadow-sm">
      <div className="ov-meta-upper text-nebula-fg-3">{label}</div>
      <div className="ov-numeric mt-2 text-3xl text-nebula-fg-1">{value}</div>
      {sub && <div className="ov-meta mt-1 text-nebula-fg-3">{sub}</div>}
    </div>
  );
}

/** Compact "12 min ago" / "3 h ago" / "2 d ago" formatter. Pure function;
 *  no Intl.RelativeTimeFormat to keep the bundle lean and the output stable
 *  for the OneView aesthetic. */
function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 0) return 'just now';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} d ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon} mo ago`;
  return `${Math.floor(mon / 12)} y ago`;
}

// ---------- Sort helper -----------------------------------------------------

type SortColumn = 'filename' | 'author' | 'chunkCount' | 'lastIngestedAt';

type SourceKindFilter = 'uploads' | 'confluence' | 'code' | 'all';

type Classification = 'public' | 'internal' | 'confidential' | 'restricted';

type ClassificationFilter = Classification | 'all';

/** Read a doc's classification, defaulting unset values to `internal`
 *  to match the practitioner /documents page. */
function docClassification(d: DocumentRegistryEntry): Classification {
  const raw = (d as DocumentRegistryEntry & { classification?: string }).classification;
  if (raw === 'public' || raw === 'confidential' || raw === 'restricted') return raw;
  return 'internal';
}

/**
 * Classify a registry row by where it came from. The same
 * `kennisbank_ring{0,1}_documents` collection holds operator uploads,
 * Confluence-synced pages and Code-Sources-synced files; the source
 * marker is whichever foreign-key field is set on the row.
 *
 *   confluenceSourceId set → 'confluence'
 *   codeSourceId set       → 'code'
 *   otherwise              → 'uploads'
 */
function docSourceKind(d: DocumentRegistryEntry): 'uploads' | 'confluence' | 'code' {
  const rec = d as DocumentRegistryEntry & {
    confluenceSourceId?: string;
    codeSourceId?: string;
  };
  if (rec.confluenceSourceId) return 'confluence';
  if (rec.codeSourceId) return 'code';
  return 'uploads';
}

function docConfidentialKind(d: DocumentRegistryEntry): 'internal' | 'vendor' | 'client' {
  const raw = (d as DocumentRegistryEntry & { confidentialKind?: 'internal' | 'vendor' | 'client' })
    .confidentialKind;
  return raw ?? 'internal';
}

function docProductKind(d: DocumentRegistryEntry): 'oneview' | 'stactize' | 'surge' | undefined {
  return (d as DocumentRegistryEntry & { productKind?: 'oneview' | 'stactize' | 'surge' })
    .productKind;
}

function filterDocuments(
  docs: DocumentRegistryEntry[],
  sourceKind: SourceKindFilter,
  ringFilter: 'all' | 'ring0' | 'ring1',
  classificationFilter: ClassificationFilter,
  confidentialKindFilter: 'all' | 'internal' | 'vendor' | 'client',
  productKindFilter: 'all' | 'oneview' | 'stactize' | 'surge',
  q: string,
): DocumentRegistryEntry[] {
  const needle = q.trim().toLowerCase();
  return docs.filter((d) => {
    if (sourceKind !== 'all' && docSourceKind(d) !== sourceKind) return false;
    if (ringFilter !== 'all') {
      const ring = d.ring ?? 'ring1';
      if (ring !== ringFilter) return false;
    }
    if (classificationFilter !== 'all') {
      if (docClassification(d) !== classificationFilter) return false;
    }
    if (classificationFilter === 'confidential' && confidentialKindFilter !== 'all') {
      if (docConfidentialKind(d) !== confidentialKindFilter) return false;
    }
    // productKind filter only applies to product (non-confidential ring1) docs.
    if (productKindFilter !== 'all') {
      const isProduct = (d.ring ?? 'ring1') === 'ring1' && docClassification(d) !== 'confidential';
      if (!isProduct) return false;
      if (docProductKind(d) !== productKindFilter) return false;
    }
    if (needle) {
      const hay = `${d.filename} ${d.documentTitle ?? ''} ${d.author ?? ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
}

function countByClassification(docs: DocumentRegistryEntry[]): Record<Classification, number> {
  const out: Record<Classification, number> = {
    public: 0,
    internal: 0,
    confidential: 0,
    restricted: 0,
  };
  for (const d of docs) {
    out[docClassification(d)] += 1;
  }
  return out;
}

function countBySourceKind(docs: DocumentRegistryEntry[]): {
  uploads: number;
  confluence: number;
  code: number;
} {
  let uploads = 0;
  let confluence = 0;
  let code = 0;
  for (const d of docs) {
    const k = docSourceKind(d);
    if (k === 'uploads') uploads += 1;
    else if (k === 'confluence') confluence += 1;
    else code += 1;
  }
  return { uploads, confluence, code };
}

function SourceFilterChip({
  label,
  hint,
  count,
  active,
  onClick,
}: {
  label: string;
  hint: string;
  count: number;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={hint}
      className={[
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
        active
          ? 'bg-nebula-primary text-white'
          : 'border border-nebula-border-soft bg-nebula-surface text-nebula-fg-2 hover:border-nebula-border hover:text-nebula-fg-1',
      ].join(' ')}
    >
      {label}
      <span
        className={[
          'rounded-full px-1.5 py-0 font-numeric text-[10px] font-semibold',
          active ? 'bg-white/20 text-white' : 'bg-nebula-gray-5 text-nebula-fg-3',
        ].join(' ')}
      >
        {count}
      </span>
    </button>
  );
}

function RingFilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
        active
          ? 'bg-nebula-tint text-nebula-primary'
          : 'border border-nebula-border-soft bg-nebula-surface text-nebula-fg-3 hover:text-nebula-fg-1',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function sortDocuments(
  docs: DocumentRegistryEntry[],
  by: SortColumn,
  dir: 'asc' | 'desc',
): DocumentRegistryEntry[] {
  const sign = dir === 'asc' ? 1 : -1;
  const out = [...docs];
  out.sort((a, b) => {
    let cmp = 0;
    switch (by) {
      case 'filename':
        cmp = a.filename.toLowerCase().localeCompare(b.filename.toLowerCase());
        break;
      case 'author':
        cmp = a.author.toLowerCase().localeCompare(b.author.toLowerCase());
        break;
      case 'chunkCount':
        cmp = a.chunkCount - b.chunkCount;
        break;
      case 'lastIngestedAt':
        cmp = (a.lastIngestedAt ?? '').localeCompare(b.lastIngestedAt ?? '');
        break;
    }
    return cmp * sign;
  });
  return out;
}

async function openDocument(
  filename: string,
  setTopError: (s: string | null) => void,
): Promise<void> {
  // Confluence-ingested docs have no canonical .md to download — the
  // source of truth is the Confluence page itself. Open it in a new tab.
  const confluenceMatch = /^confluence-(\d+)-/.exec(filename);
  if (confluenceMatch && confluenceMatch[1]) {
    const url = `https://onenebula.atlassian.net/wiki/pages/viewpage.action?pageId=${confluenceMatch[1]}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  try {
    const res = await apiGet<{ url: string; expiresAt: string }>(
      `/api/v1/admin/knowledgebase/documents/${encodeURIComponent(filename)}/download-url`,
    );
    window.open(res.url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    if (err instanceof ApiError && err.code === 'source_not_in_bucket') {
      setTopError(
        `Source file "${filename}" is no longer in the bucket. This happens for older documents — the content is still searchable, but the original file is no longer available.`,
      );
      return;
    }
    setTopError(errorMessage(err, 'Could not open source file.'));
  }
}

// ---------- ChunksModal ----------------------------------------------------
// Read-only spot-check view for the per-doc chunk extraction. Andre clicks
// the chunkCount in the row → fetches /:filename/chunks → renders each
// chunk's text body with chunk index + page. No editing — chunks regenerate
// on every reingest, so any corrections happen via Bewerk metadata or
// re-uploading the source file.

interface ChunkRow {
  chunkId: string;
  chunkIndex: number;
  page: number | null;
  chunkText: string;
  sectionType: string | null;
  documentSection: string | null;
}

function ChunksModal({
  doc,
  onClose,
}: {
  doc: DocumentRegistryEntry;
  onClose: () => void;
}): JSX.Element {
  const [chunks, setChunks] = useState<ChunkRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiGet<{ filename: string; chunks: ChunkRow[] }>(
      `/api/v1/admin/knowledgebase/documents/${encodeURIComponent(doc.filename)}/chunks`,
    )
      .then((res) => {
        if (!cancelled) setChunks(res.chunks);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not load chunks.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [doc.filename]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="chunks-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-brand-100 px-6 py-4">
          <div>
            <h2 id="chunks-title" className="text-lg font-semibold text-ink-900">
              Chunks of {doc.documentTitle || doc.filename}
            </h2>
            <p className="mt-1 text-xs text-ink-900/60">
              {doc.chunkCount} chunks indexed in <code>kennisbank_ring1</code>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
          >
            <XIcon size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!chunks && !error && (
            <p className="py-10 text-center text-sm text-ink-900/60">Loading chunks…</p>
          )}
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          {chunks && chunks.length === 0 && !error && (
            <p className="py-10 text-center text-sm text-ink-900/60">
              No chunks found for this document.
            </p>
          )}
          {chunks && chunks.length > 0 && (
            <ol className="space-y-3">
              {chunks.map((c) => (
                <li
                  key={c.chunkId}
                  className="rounded-lg border border-brand-100 bg-brand-50/30 px-4 py-3"
                >
                  <div className="mb-1 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-ink-800/60">
                    <span>chunk {c.chunkIndex + 1}</span>
                    {c.page !== null && <span>· page {c.page}</span>}
                    {c.sectionType && <span>· {c.sectionType}</span>}
                    {c.documentSection && <span>· {c.documentSection}</span>}
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-ink-900">{c.chunkText}</p>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="border-t border-brand-100 bg-brand-50/30 px-6 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-brand-200 px-4 py-1.5 text-sm text-ink-900 transition-colors hover:bg-brand-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Per-row scope chip — replaces the binary `Ring 0 → Internal | Ring 1 → Product`
 * label with a 5-state surface that respects classification:
 *
 *   - confidential + kind=internal             → "Confidential" (red)
 *   - confidential + kind=vendor               → "Vendor" (amber)
 *   - confidential + kind=client               → "Client" (purple)
 *   - ring0 + non-confidential                 → "OneNebula Internal" (slate)
 *   - ring1 + non-confidential                 → "Product" (brand/Navy)
 *
 * Classification takes precedence over ring so confidential docs render
 * the same Vendor/Client/Confidential chip whether they live in ring0
 * (new uploads, post-migration) or ring1 (legacy docs that pre-date the
 * "confidential = ring0" decision).
 *
 * For confidential docs, clicking the chip opens a dropdown to re-pick
 * the kind inline (same UX as the specialisations / author cells).
 * Defaults to `internal` when the field is unset on an existing
 * confidential doc — matches readConfidentialKind on the backend.
 */
function ScopeChip({
  doc,
  disabled,
  onChangeKind,
  onChangeProductKind,
}: {
  doc: DocumentRegistryEntry & {
    confidentialKind?: 'internal' | 'vendor' | 'client';
    productKind?: 'oneview' | 'stactize' | 'surge';
  };
  disabled: boolean;
  onChangeKind: (kind: 'internal' | 'vendor' | 'client') => void;
  onChangeProductKind: (kind: 'oneview' | 'stactize' | 'surge') => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Classification first — confidential docs render the kind chip
  // regardless of ring. Ring-only chip falls through for non-confidential.
  const isConfidential =
    doc.classification === 'confidential' || doc.classification === 'restricted';

  if (!isConfidential && doc.ring === 'ring0') {
    return (
      <span
        title="OneNebula internal document — visible only to OneNebula employees."
        className="inline-flex flex-shrink-0 items-center rounded-md border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-800"
      >
        OneNebula Internal
      </span>
    );
  }

  if (!isConfidential) {
    // Product tier — split by OneNebula product line. Falls back to the
    // generic "Product" chip when productKind is unset (legacy docs).
    const pmeta: Record<
      'oneview' | 'stactize' | 'surge',
      { label: string; cls: string; title: string }
    > = {
      oneview: {
        label: 'OneView',
        cls: 'border-brand-300 bg-brand-50 text-brand-800',
        title: 'OneView product documentation.',
      },
      stactize: {
        label: 'Stactize',
        cls: 'border-emerald-300 bg-emerald-50 text-emerald-900',
        title: 'Stactize product documentation.',
      },
      surge: {
        label: 'Surge',
        cls: 'border-sky-300 bg-sky-50 text-sky-900',
        title: 'Surge product documentation.',
      },
    };
    const pkind = doc.productKind;
    const productCls = pkind ? pmeta[pkind].cls : 'border-brand-200 bg-brand-50 text-brand-800';
    const productLabel = pkind ? pmeta[pkind].label : 'Product';
    const productTitle = pkind
      ? `${pmeta[pkind].title} Click to change.`
      : 'OneNebula product / customer-facing documentation. Click to tag a product line.';
    return (
      <div ref={ref} className="relative inline-flex">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          title={productTitle}
          className={`inline-flex flex-shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${productCls} ${
            disabled ? 'cursor-not-allowed opacity-60' : 'hover:brightness-95'
          }`}
        >
          {productLabel}
          {!disabled && <span aria-hidden="true">▾</span>}
        </button>
        {open && (
          <div className="absolute left-0 top-full z-30 mt-1 w-32 overflow-hidden rounded-md border border-ink-200 bg-white shadow-lg">
            {(['oneview', 'stactize', 'surge'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setOpen(false);
                  if (k !== pkind) onChangeProductKind(k);
                }}
                className={`flex w-full items-center justify-between px-2 py-1.5 text-left text-[11px] ${
                  k === pkind ? 'bg-brand-50 font-semibold text-brand-900' : 'hover:bg-ink-50'
                }`}
              >
                <span>{pmeta[k].label}</span>
                {k === pkind && <span aria-hidden="true">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const kind: 'internal' | 'vendor' | 'client' = doc.confidentialKind ?? 'internal';
  const meta: Record<
    'internal' | 'vendor' | 'client',
    { label: string; cls: string; title: string }
  > = {
    internal: {
      label: 'Confidential',
      cls: 'border-rose-300 bg-rose-50 text-rose-800',
      title: 'OneNebula internal-confidential (board, strategy).',
    },
    vendor: {
      label: 'Vendor',
      cls: 'border-amber-300 bg-amber-50 text-amber-900',
      title: 'Vendor / supplier contract or proposal.',
    },
    client: {
      label: 'Client',
      cls: 'border-violet-300 bg-violet-50 text-violet-900',
      title: 'Client contract, proposal, or NDA.',
    },
  };
  const current = meta[kind];

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={`${current.title} Click to change.`}
        className={`inline-flex flex-shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${current.cls} ${
          disabled ? 'cursor-not-allowed opacity-60' : 'hover:brightness-95'
        }`}
      >
        {current.label}
        {!disabled && <span aria-hidden="true">▾</span>}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-36 overflow-hidden rounded-md border border-ink-200 bg-white shadow-lg">
          {(['internal', 'vendor', 'client'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setOpen(false);
                if (k !== kind) onChangeKind(k);
              }}
              className={`flex w-full items-center justify-between px-2 py-1.5 text-left text-[11px] ${
                k === kind ? 'bg-petrol-50 font-semibold text-petrol-900' : 'hover:bg-ink-50'
              }`}
            >
              <span>{meta[k].label}</span>
              {k === kind && <span aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
